import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CompetitionDay,
  Entry,
  EventReadiness,
  Level,
} from '@wht-xc-utils/shared/domain';
import {
  CANONICAL_LEVELS,
  parseRideTime,
  type LevelSlug,
} from '@wht-xc-utils/shared/domain';
import type { DerivedSchedule } from '@wht-xc-utils/shared/scheduling';

import { EventTabs } from '../components/EventTabs';
import { api } from '../lib/api';

interface DeriveScheduleSummary extends DerivedSchedule {
  daysCreated: number;
  daysUpdated: number;
  levelsScheduled: number;
}

export function SchedulePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const qc = useQueryClient();

  const days = useQuery({
    queryKey: ['days', eventId],
    queryFn: () => api.get<{ days: CompetitionDay[] }>(`/api/events/${eventId}/days`),
    enabled: !!eventId,
  });
  const levels = useQuery({
    queryKey: ['levels', eventId],
    queryFn: () => api.get<{ levels: Level[] }>(`/api/events/${eventId}/levels`),
    enabled: !!eventId,
  });
  const entries = useQuery({
    queryKey: ['entries', eventId],
    queryFn: () => api.get<{ entries: Entry[] }>(`/api/events/${eventId}/entries`),
    enabled: !!eventId,
  });

  const [newDayDate, setNewDayDate] = useState('');
  const [newDayLabel, setNewDayLabel] = useState('');
  const [newLevelSlug, setNewLevelSlug] = useState<LevelSlug>('beginner-novice');

  const markScheduleViewed = useMutation({
    mutationFn: () =>
      api.post<{ readiness: EventReadiness }>(
        `/api/events/${eventId}/workflow/schedule-viewed`,
      ),
    onSuccess: (data) => {
      qc.setQueryData(['eventReadiness', eventId], data);
    },
  });

  const markScheduleViewedMutate = markScheduleViewed.mutate;
  useEffect(() => {
    if (eventId) markScheduleViewedMutate();
  }, [eventId, markScheduleViewedMutate]);

  const deriveSchedule = useMutation({
    mutationFn: () =>
      api.post<{ summary: DeriveScheduleSummary }>(
        `/api/events/${eventId}/schedule/derive`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['days', eventId] });
      qc.invalidateQueries({ queryKey: ['levels', eventId] });
      qc.invalidateQueries({ queryKey: ['eventReadiness', eventId] });
    },
  });

  const autoDeriveAttempted = useRef(false);
  const deriveScheduleMutate = deriveSchedule.mutate;
  useEffect(() => {
    if (
      autoDeriveAttempted.current ||
      !entries.isSuccess ||
      !days.isSuccess ||
      !levels.isSuccess
    ) {
      return;
    }
    const hasEntries = entries.data.entries.length > 0;
    const hasLevels = levels.data.levels.length > 0;
    const hasScheduledLevel = levels.data.levels.some(
      (level) => level.competitionDayId && level.runOrder != null,
    );
    if (hasEntries && hasLevels && (!days.data.days.length || !hasScheduledLevel)) {
      autoDeriveAttempted.current = true;
      deriveScheduleMutate();
    }
  }, [
    days.data,
    days.isSuccess,
    deriveScheduleMutate,
    entries.data,
    entries.isSuccess,
    levels.data,
    levels.isSuccess,
  ]);

  const createDay = useMutation({
    mutationFn: () =>
      api.post<{ day: CompetitionDay }>(`/api/events/${eventId}/days`, {
        date: newDayDate,
        label: newDayLabel,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['days', eventId] });
      qc.invalidateQueries({ queryKey: ['eventReadiness', eventId] });
      setNewDayDate('');
      setNewDayLabel('');
    },
  });

  const updateDay = useMutation({
    mutationFn: (input: { id: string; label: string }) =>
      api.patch<{ day: CompetitionDay }>(
        `/api/events/${eventId}/days/${input.id}`,
        { label: input.label },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['days', eventId] }),
  });

  const deleteDay = useMutation({
    mutationFn: (id: string) => api.delete(`/api/events/${eventId}/days/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['days', eventId] });
      qc.invalidateQueries({ queryKey: ['levels', eventId] });
      qc.invalidateQueries({ queryKey: ['eventReadiness', eventId] });
    },
  });

  const createLevel = useMutation({
    mutationFn: (canonicalSlug: LevelSlug) =>
      api.post<{ level: Level }>(`/api/events/${eventId}/levels`, {
        canonicalSlug,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['levels', eventId] });
      qc.invalidateQueries({ queryKey: ['eventReadiness', eventId] });
    },
  });

  const updateLevel = useMutation({
    mutationFn: (input: {
      id: string;
      competitionDayId?: string | null;
      runOrder?: number | null;
      volunteerShiftNumber?: number;
    }) =>
      api.patch<{ level: Level }>(`/api/events/${eventId}/levels/${input.id}`, {
        competitionDayId: input.competitionDayId,
        runOrder: input.runOrder,
        volunteerShiftNumber: input.volunteerShiftNumber,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['levels', eventId] });
      qc.invalidateQueries({ queryKey: ['eventReadiness', eventId] });
    },
  });

  const deleteLevel = useMutation({
    mutationFn: (id: string) => api.delete(`/api/events/${eventId}/levels/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['levels', eventId] });
      qc.invalidateQueries({ queryKey: ['eventReadiness', eventId] });
    },
  });

  const existingSlugs = new Set((levels.data?.levels ?? []).map((l) => l.canonicalSlug));
  const availableSlugs = CANONICAL_LEVELS.filter((l) => !existingSlugs.has(l.slug));
  const selectedLevelSlug =
    availableSlugs.find((level) => level.slug === newLevelSlug)?.slug ??
    availableSlugs[0]?.slug ??
    null;
  const levelById = new Map(
    (levels.data?.levels ?? []).map((level) => [level.id, level]),
  );
  const scheduleIssues = (entries.data?.entries ?? [])
    .filter((entry) => entry.status === 'Accepted')
    .map((entry) => {
      const issues: string[] = [];
      if (!entry.levelId) issues.push('No level mapping');
      if (!entry.xcRideTime && !parseRideTime(entry.xcRideTimeRaw)) {
        issues.push('No usable XC ride time');
      }
      return { entry, issues };
    })
    .filter(({ issues }) => issues.length > 0);

  const sortedDays = useMemo(
    () => [...(days.data?.days ?? [])].sort((a, b) => a.dayOrder - b.dayOrder),
    [days.data],
  );

  const levelsByDay = useMemo(() => {
    const m = new Map<string | null, Level[]>();
    for (const l of levels.data?.levels ?? []) {
      const key = l.competitionDayId ?? null;
      const list = m.get(key) ?? [];
      list.push(l);
      m.set(key, list);
    }
    for (const list of m.values()) {
      list.sort((a, b) => {
        const ar = a.runOrder ?? Number.POSITIVE_INFINITY;
        const br = b.runOrder ?? Number.POSITIVE_INFINITY;
        if (ar !== br) return ar - br;
        return a.displayName.localeCompare(b.displayName);
      });
    }
    return m;
  }, [levels.data]);

  const renderLevelTable = (rows: Level[]) => (
    <table className="aligned-table">
      <colgroup>
        <col style={{ width: '30%' }} />
        <col style={{ width: '28%' }} />
        <col style={{ width: '16%' }} />
        <col style={{ width: '16%' }} />
        <col style={{ width: '10%' }} />
      </colgroup>
      <thead>
        <tr>
          <th>Level</th>
          <th>Day</th>
          <th>Run order</th>
          <th>Volunteer shift</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={l.id}>
            <td>{l.displayName}</td>
            <td>
              <select
                value={l.competitionDayId ?? ''}
                onChange={(e) =>
                  updateLevel.mutate({
                    id: l.id,
                    competitionDayId: e.target.value || null,
                  })
                }
              >
                <option value="">(unassigned)</option>
                {sortedDays.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.label || d.date}
                  </option>
                ))}
              </select>
            </td>
            <td>
              <input
                type="number"
                style={{ maxWidth: 90 }}
                value={l.runOrder ?? ''}
                onChange={(e) =>
                  updateLevel.mutate({
                    id: l.id,
                    runOrder: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />
            </td>
            <td>
              <input
                type="number"
                min={1}
                step={1}
                style={{ maxWidth: 90 }}
                value={l.volunteerShiftNumber}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (!Number.isInteger(value) || value < 1) return;
                  updateLevel.mutate({
                    id: l.id,
                    volunteerShiftNumber: value,
                  });
                }}
              />
            </td>
            <td className="right">
              <button
                className="subtle danger"
                onClick={() => {
                  if (confirm(`Delete ${l.displayName}? Jumps go too.`)) {
                    deleteLevel.mutate(l.id);
                  }
                }}
              >
                ✕
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const unassignedLevels = levelsByDay.get(null) ?? [];
  const hasAnyLevel = (levels.data?.levels ?? []).length > 0;

  return (
    <>
      <EventTabs />

      <div className="card">
        <h2>Derive schedule from entries</h2>
        <p className="muted">
          On first visit, competition days and level order are created
          automatically from accepted XC ride times. Re-derive after refreshing
          entries to apply changed times. Existing custom day labels are
          preserved.
        </p>
        <div className="toolbar">
          <button
            className="primary"
            disabled={
              (entries.data?.entries.length ?? 0) === 0 ||
              deriveSchedule.isPending
            }
            onClick={() => deriveSchedule.mutate()}
          >
            {deriveSchedule.isPending
              ? 'Deriving…'
              : 'Re-derive days and run order'}
          </button>
          {deriveSchedule.data ? (
            <span className="success">
              Scheduled {deriveSchedule.data.summary.levelsScheduled} levels
              across {deriveSchedule.data.summary.days.length} days.
            </span>
          ) : null}
          {deriveSchedule.error ? (
            <span className="error">
              {(deriveSchedule.error as Error).message}
            </span>
          ) : null}
        </div>
        {deriveSchedule.data?.summary.levelDateConflicts.length ? (
          <div className="notice">
            Some levels had accepted ride times on multiple dates. The date
            with the most entries was selected:
            <ul>
              {deriveSchedule.data.summary.levelDateConflicts.map((conflict) => (
                <li key={conflict.levelId}>
                  {levelById.get(conflict.levelId)?.displayName ??
                    conflict.levelId}{' '}
                  → {conflict.selectedDate}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {markScheduleViewed.error ? (
          <p className="error">
            Could not record schedule review:{' '}
            {(markScheduleViewed.error as Error).message}
          </p>
        ) : null}
      </div>

      <div className="card">
        <h2>Schedule diagnostics</h2>
        {scheduleIssues.length === 0 ? (
          <p className="success">
            Every accepted entry has a level mapping and XC ride time.
          </p>
        ) : (
          <>
            <p className="muted">
              These accepted entries cannot be fully placed in the derived
              schedule. Fix level mappings on the Entries tab or refresh the
              ShowConnect data.
            </p>
            <table>
              <thead>
                <tr>
                  <th>Division</th>
                  <th>Rider</th>
                  <th>Horse</th>
                  <th>Issue</th>
                </tr>
              </thead>
              <tbody>
                {scheduleIssues.map(({ entry, issues }) => (
                  <tr key={entry.id}>
                    <td>{entry.divisionName || '—'}</td>
                    <td>{entry.riderName || '—'}</td>
                    <td>{entry.horseName || '—'}</td>
                    <td>
                      {issues.map((issue) => (
                        <span
                          key={issue}
                          className="pill warn"
                          style={{ marginRight: 4 }}
                        >
                          {issue}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="workflow-actions">
              <Link className="button-link" to={`/events/${eventId}/entries`}>
                Review entry mappings
              </Link>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2>Competition days</h2>
        <div className="toolbar">
          <input
            type="date"
            value={newDayDate}
            onChange={(e) => setNewDayDate(e.target.value)}
            style={{ maxWidth: 180 }}
          />
          <input
            type="text"
            placeholder="Label (e.g. XC Day 1 / Saturday)"
            value={newDayLabel}
            onChange={(e) => setNewDayLabel(e.target.value)}
            style={{ maxWidth: 280 }}
          />
          <button
            className="primary"
            disabled={!newDayDate || createDay.isPending}
            onClick={() => createDay.mutate()}
          >
            Add day
          </button>
        </div>
        {sortedDays.length === 0 ? (
          <p className="muted">No competition days yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Order</th>
                <th>Date</th>
                <th>Label</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sortedDays.map((d) => (
                <tr key={d.id}>
                  <td>{d.dayOrder}</td>
                  <td>{d.date}</td>
                  <td>
                    <DayLabelInput
                      day={d}
                      onSave={(label) => updateDay.mutate({ id: d.id, label })}
                    />
                  </td>
                  <td className="right">
                    <button
                      className="subtle danger"
                      onClick={() => {
                        if (confirm(`Delete ${d.date}?`)) deleteDay.mutate(d.id);
                      }}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h2>Add level</h2>
        <div className="toolbar">
          <select
            value={selectedLevelSlug ?? ''}
            onChange={(e) => setNewLevelSlug(e.target.value as LevelSlug)}
            disabled={availableSlugs.length === 0}
          >
            {availableSlugs.map((l) => (
              <option key={l.slug} value={l.slug}>
                {l.displayName}
              </option>
            ))}
          </select>
          <button
            className="primary"
            disabled={availableSlugs.length === 0 || createLevel.isPending}
            onClick={() => {
              if (selectedLevelSlug) createLevel.mutate(selectedLevelSlug);
            }}
          >
            Add level
          </button>
          {createLevel.error ? (
            <span className="error">{(createLevel.error as Error).message}</span>
          ) : null}
        </div>
        {!hasAnyLevel ? (
          <p className="muted">
            No levels yet. Add them here, or they'll be created automatically from imported
            entries.
          </p>
        ) : null}
      </div>

      {sortedDays.map((d) => {
        const rows = levelsByDay.get(d.id) ?? [];
        return (
          <div key={d.id} className="card">
            <h2>
              {d.label || d.date}{' '}
              <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>
                ({d.date} • {rows.length} level{rows.length === 1 ? '' : 's'})
              </span>
            </h2>
            {rows.length === 0 ? (
              <p className="muted">No levels assigned to this day yet.</p>
            ) : (
              renderLevelTable(rows)
            )}
          </div>
        );
      })}

      {unassignedLevels.length > 0 ? (
        <div className="card">
          <h2>
            Unassigned{' '}
            <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>
              ({unassignedLevels.length} level{unassignedLevels.length === 1 ? '' : 's'})
            </span>
          </h2>
          <p className="muted">Pick a competition day for each to schedule them.</p>
          {renderLevelTable(unassignedLevels)}
        </div>
      ) : null}

      <div className="card">
        <h2>Next: add jumps and volunteers</h2>
        <p className="muted">
          These inputs are independent once the schedule has been reviewed, so
          you can complete them in either order.
        </p>
        <div className="workflow-actions">
          <Link
            className="button-link primary"
            to={`/events/${eventId}/volunteers`}
          >
            Continue to volunteers
          </Link>
          <Link
            className="button-link primary"
            to={`/events/${eventId}/jumps`}
          >
            Continue to jumps
          </Link>
        </div>
      </div>
    </>
  );
}

function DayLabelInput({
  day,
  onSave,
}: {
  day: CompetitionDay;
  onSave: (label: string) => void;
}) {
  const [label, setLabel] = useState(day.label);

  useEffect(() => {
    setLabel(day.label);
  }, [day.label]);

  return (
    <input
      type="text"
      value={label}
      aria-label={`Label for ${day.date}`}
      onChange={(e) => setLabel(e.target.value)}
      onBlur={() => {
        const next = label.trim();
        if (next !== day.label) onSave(next);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
    />
  );
}
